import { useState } from "react";
import { toast } from "sonner";
import { Flag, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/api";

export function ReportDialog({ jobId, reported, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (reported) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#B0413E]" data-testid={`reported-${jobId}`}>
        <Flag className="w-3.5 h-3.5" />{t("report.done")}
      </span>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/report`, { reason });
      toast.success(data.auto_confirmed ? t("report.autoConfirmed") : t("report.received"));
      setOpen(false);
      onDone && onDone(jobId);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`report-btn-${jobId}`}
          className="rounded-full gap-1.5 text-[#B0413E] hover:bg-[#B0413E]/10">
          <Flag className="w-4 h-4" />{t("report.button")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{t("report.title")}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#8C7A70]">{t("report.desc")}</p>
        <Textarea data-testid="report-reason" rows={4} value={reason}
          onChange={(e) => setReason(e.target.value)} placeholder={t("report.placeholder")} className="rounded-lg" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">{t("report.cancel")}</Button>
          <Button data-testid="report-submit" onClick={submit} disabled={busy}
            className="rounded-full bg-[#B0413E] hover:bg-[#8f3230] text-white">{t("report.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TrustBadge({ trust, hint }) {
  const { t } = useI18n();
  if (!trust) return null;
  const flagged = trust.flagged;
  const verified = trust.verified && !flagged;
  const cls = flagged
    ? "bg-[#B0413E]/10 text-[#B0413E]"
    : verified
    ? "bg-[#5C6646]/10 text-[#5C6646]"
    : "bg-[#E69625]/15 text-[#B27A1E]";
  const label = flagged ? t("trust.flagged") : verified ? t("trust.verified") : t("trust.new");
  const since = trust.member_since ? String(trust.member_since).slice(0, 10) : null;
  return (
    <div className="mt-6 rounded-2xl border border-[#EBDDD3] bg-white p-6" data-testid="customer-trust">
      <h2 className="font-semibold text-[#2D231F] mb-3">{t("trust.title")}</h2>
      <div className="flex flex-wrap items-center gap-3 text-sm text-[#5A4B45]">
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`} data-testid="trust-badge">{label}</span>
        <span>{trust.jobs_posted} {t("trust.jobsPosted")}</span>
        {since && <span className="text-[#8C7A70]">{t("trust.memberSince")} {since}</span>}
      </div>
      {hint && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#E69625]/10 border border-[#E69625]/30 px-3 py-2.5 text-xs text-[#B27A1E]" data-testid="trust-hint">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{t("trust.hint")}</span>
        </div>
      )}
    </div>
  );
}
